import { Component, Emit, PropSync, Vue, Watch } from 'vue-property-decorator'
import {
    SynopticTemplateNodeChildrenSelectionType,
    SynopticTemplateNodeType
} from '@/modules/configurationModule/detail/templates/synoptic-templates/services/configuration-synoptic-template/configuration-synoptic-template.dto'
import {
    initialSynopticTemplatesModalStructureFormState,
    SynopticTemplatesModalStructureFormState,
    nodeChildrenSelectionTypeTranslations,
    nodeTypeTranslations
} from '@/modules/configurationModule/detail/templates/synoptic-templates/components/modal-structure-form/configuration-templates-synoptic-templates-modal-structure-form.model'
import { uniqueId } from 'lodash'
import { StrictNullable } from '@/shared/types/app.types'
import {
    defaultSynopticTemplateTreeNodeStructure,
    SynopticTemplateTreeNode
} from '@/modules/configurationModule/detail/templates/synoptic-templates/components/modal-structure/configuration-templates-synoptic-templates-modal-structure.model'

@Component
export default class ConfigurationTemplatesSynopticTemplatesModalStructureForm extends Vue {
    @PropSync('treeNode') syncedTreeNode!: StrictNullable<SynopticTemplateTreeNode>

    @Watch('treeNode')
    async nodeTreeSelectedChanged(treeNode: StrictNullable<SynopticTemplateTreeNode>): Promise<void> {
        await this.$validator.reset()

        if (treeNode) {
            this.state = {
                title: treeNode.data.text,
                required: treeNode.data.required,
                code: treeNode.data.code,
                nodeType: treeNode.data.nodeType,
                nodeChildrenSelectionType: treeNode.data.allowsChildrenSelection
            }
        }
    }

    @Emit()
    change(): void {}

    state: SynopticTemplatesModalStructureFormState

    private get _treeModel(): SynopticTemplateTreeNode[] {
        return this.syncedTreeNode?.tree.model ?? []
    }

    get nodeTypes(): string[] {
        return Object.keys(SynopticTemplateNodeType)
    }
    get nodeChildrenSelectionTypes(): string[] {
        if (this.syncedTreeNode?.hasChildren()) {
            return Object.keys(SynopticTemplateNodeChildrenSelectionType)
        }
        return [SynopticTemplateNodeChildrenSelectionType.NONE]
    }

    get hasSelectedTreeNode(): boolean {
        return !!this.syncedTreeNode
    }
    get canCheckRequiredField(): boolean {
        return this.state.nodeChildrenSelectionType !== SynopticTemplateNodeChildrenSelectionType.NONE &&
            !!this.syncedTreeNode?.hasChildren()
    }
    get canAddSibling(): boolean {
        return this.hasSelectedTreeNode
    }
    get canAddChild(): boolean {
        return this.hasSelectedTreeNode
    }
    get canDelete(): boolean {
        if (this.syncedTreeNode) {
            const rootNodes = this._treeModel.filter(treeNode => treeNode.isRoot())
            return !this.syncedTreeNode.isRoot() || rootNodes.length > 1
        }
        return false
    }

    get modelValidated(): boolean {
        const hasErrors = !!this.errors.count()
        const isDirty = this.$_someFieldIsDirty(this.fields)
        const hasTitle = !!this.state.title.length
        return !hasErrors &&
            isDirty &&
            hasTitle &&
            this.hasSelectedTreeNode
    }

    constructor() {
        super()
        this.state = initialSynopticTemplatesModalStructureFormState()
    }

    getSynopticTemplateNodeTypeLabel(nodeType: SynopticTemplateNodeType): string {
        return this.$tc(nodeTypeTranslations[nodeType])
    }
    getSynopticTemplateNodeChildrenSelectionTypeLabel(nodeChildrenSelectionType: SynopticTemplateNodeChildrenSelectionType): string {
        return this.$tc(nodeChildrenSelectionTypeTranslations[nodeChildrenSelectionType])
    }

    addSibling(): void {
        this.syncedTreeNode?.after(defaultSynopticTemplateTreeNodeStructure())
        this.change() // emits an event
    }

    addChild(): void {
        if (this.syncedTreeNode) {
            this.syncedTreeNode.append(defaultSynopticTemplateTreeNodeStructure())
            this.syncedTreeNode.expand()
            this.change() // emits an event
        }
    }

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelText = this.$tc('configuration.modal.synopticTemplate.deleteModalConfirmationTreeElements')
        const labelDeleteQuestion = this.$tc('configuration.grid.deleteQuestion')
        const labelCancelAction = this.$tc('component.modalConfirmation.okTitle')
        const labelAcceptAction = this.$tc('component.modalConfirmation.cancelTitle')
        return this.$_showConfirmationModal(
            `${labelText}, ${labelDeleteQuestion}`,
            labelCancelAction,
            labelAcceptAction
        )
    }
    async remove(): Promise<void> {
        if (this.syncedTreeNode) {
            if (!this.syncedTreeNode.hasChildren()) {
                this.syncedTreeNode.remove()
                this.change() // emits an event
            } else {
                if (await this._showModalAdviceWhenDelete()) {
                    this.syncedTreeNode.remove()
                    this.change() // emits an event
                }
            }
        }
    }

    async save(): Promise<void> {
        if (this.syncedTreeNode) {
            this.syncedTreeNode.data = {
                nodeType: this.state.nodeType,
                allowsChildrenSelection: this.state.nodeChildrenSelectionType,
                text: this.state.title,
                code: this.state.code.length ? this.state.code : uniqueId(),
                morphologicCodeId: this.syncedTreeNode.data.morphologicCodeId,
                topographicCodeId: this.syncedTreeNode.data.topographicCodeId,
                required: !this.canCheckRequiredField ? false : this.state.required,
                children: this.syncedTreeNode.data.children
            }
            this.syncedTreeNode.text = this.syncedTreeNode.data.text

            await this.$validator.reset()
            this.change() // emits an event
        }
    }
}
